/** Canonical enumerations shared by API, storefront and admin. */

export const LOCALES = ['pl', 'uk'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'pl';

export const SUPPLIER_PRODUCT_STATUS = [
  'new',
  'needs_review',
  'mapped',
  'published',
  'archived',
] as const;
export type SupplierProductStatus = (typeof SUPPLIER_PRODUCT_STATUS)[number];

/** Why a product cannot be published. Drives the admin blocked-products queue. */
export const REVIEW_REASON = [
  'missing_cost_price',
  'missing_stock_row',
  'unmapped_category',
  'invalid_barcode',
  'title_truncated',
  'volume_mismatch',
  'price_delta_guard',
  'price_decrease',
  'duplicate_key',
  'broken_image',
] as const;
export type ReviewReason = (typeof REVIEW_REASON)[number];

export const IMPORT_RUN_STATUS = [
  'pending',
  'running',
  'success',
  'partial',
  'aborted',
  'failed',
] as const;
export type ImportRunStatus = (typeof IMPORT_RUN_STATUS)[number];

export const FEED_TYPE = ['csv', 'xml', 'xlsx', 'api'] as const;
export type FeedType = (typeof FEED_TYPE)[number];

export const FEED_ROLE = ['catalog', 'stock', 'price', 'barcode'] as const;
export type FeedRole = (typeof FEED_ROLE)[number];

/**
 * Availability is deliberately not derived from `stock > 0`.
 * `made_to_order` is what a null stock quantity means — see the stock section of the spec.
 */
export const AVAILABILITY = [
  'in_stock',
  'low_stock',
  'made_to_order',
  'unavailable',
] as const;
export type Availability = (typeof AVAILABILITY)[number];

export const ORDER_STATUS = [
  'new',
  'paid',
  'sent_to_supplier',
  'shipped',
  'delivered',
  'cancelled',
  'refunded',
] as const;
export type OrderStatus = (typeof ORDER_STATUS)[number];

export const SUPPLIER_ORDER_STATUS = [
  'draft',
  'awaiting_confirmation',
  'transmitted',
  'acknowledged',
  'failed',
  'cancelled',
] as const;
export type SupplierOrderStatus = (typeof SUPPLIER_ORDER_STATUS)[number];

export const RETURN_STATUS = [
  'requested',
  'approved',
  'rejected',
  'in_transit',
  'received',
  'refunded',
] as const;
export type ReturnStatus = (typeof RETURN_STATUS)[number];

export const USER_ROLE = ['admin', 'manager', 'customer'] as const;
export type UserRole = (typeof USER_ROLE)[number];

export const TRANSLATION_STATUS = ['missing', 'draft', 'reviewed'] as const;
export type TranslationStatus = (typeof TRANSLATION_STATUS)[number];

export const PRICING_SCOPE = ['global', 'supplier', 'category', 'product'] as const;
export type PricingScope = (typeof PRICING_SCOPE)[number];

export const MARKUP_TYPE = ['percent', 'fixed'] as const;
export type MarkupType = (typeof MARKUP_TYPE)[number];

/** Artisan ships these three verbatim, misspelling included. Mapped, never corrected upstream. */
export const SHIPPING_PROFILE_CODE = ['parcel', 'one_man', 'palett', 'adams_parcels', 'manual_parcel'] as const;
export type ShippingProfileCode = (typeof SHIPPING_PROFILE_CODE)[number];

export const ASSEMBLY_TYPE = ['knock_down', 'fully_assembled', 'knock_down_legs'] as const;
export type AssemblyType = (typeof ASSEMBLY_TYPE)[number];

export const PAYMENT_METHOD = ['blik', 'card', 'transfer'] as const;
export type PaymentMethod = (typeof PAYMENT_METHOD)[number];

export const PAYMENT_STATUS = ['pending', 'authorised', 'paid', 'failed', 'refunded'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUS)[number];

/** Entities that can carry translations. One table, not name_pl/name_uk columns. */
export const TRANSLATABLE_ENTITY = [
  'product',
  'category',
  'attribute',
  'attribute_value',
  'option',
  'option_value',
  'color',
  'shipping_profile',
  'assembly_type',
  'order_status',
  'email_template',
  'content_page',
] as const;
export type TranslatableEntity = (typeof TRANSLATABLE_ENTITY)[number];
